import React, { useCallback, useEffect, useState } from 'react';
import {
  addPlaceholderPlayer,
  listPlaceholderPlayers,
  sendClaimInvite,
  setPlaceholderContact,
} from '../../services/api/userService';

const msg = (err, fallback) => err?.response?.data?.message || fallback;

/** One player who hasn't joined: email / phone, save, send (or resend) the claim link. */
function PlaceholderRow({ teamId, player, onChanged }) {
  const [email, setEmail] = useState(player.contactEmail || '');
  const [phone, setPhone] = useState(player.contactPhone || '');
  const [note, setNote] = useState(null); // { kind: 'ok' | 'error', text, mergeAvailable? }
  const [busy, setBusy] = useState(false);

  const save = async (mergeWithExisting = false) => {
    setBusy(true);
    setNote(null);
    try {
      await setPlaceholderContact(teamId, player.id, { email, phone, ...(mergeWithExisting ? { mergeWithExisting: true } : {}) });
      setNote({ kind: 'ok', text: mergeWithExisting ? 'Will be merged into that account once it accepts the invite.' : 'Saved' });
      onChanged();
    } catch (err) {
      setNote({ kind: 'error', text: msg(err, 'Could not save'), mergeAvailable: err?.response?.data?.mergeAvailable === true });
    } finally {
      setBusy(false);
    }
  };

  const invite = async () => {
    setBusy(true);
    setNote(null);
    try {
      const res = await sendClaimInvite(teamId, player.id);
      setNote({ kind: 'ok', text: res.message || 'Invite sent' });
      onChanged();
    } catch (err) {
      setNote({ kind: 'error', text: msg(err, 'Could not send the invite') });
    } finally {
      setBusy(false);
    }
  };

  const canInvite = Boolean(player.contactEmail || player.mergeTargetUserId);
  return (
    <li className="claim-invites__row">
      <strong>{player.name}</strong>
      {player.invitedAt && <span className="claim-invites__sent"> · invited {new Date(player.invitedAt).toLocaleDateString()}</span>}
      <div className="claim-invites__fields">
        <input aria-label={`Email for ${player.name}`} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <input aria-label={`Phone for ${player.name}`} type="tel" placeholder="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <button type="button" onClick={() => save()} disabled={busy}>Save</button>
        <button type="button" onClick={invite} disabled={busy || !canInvite} title={canInvite ? '' : 'Add an email first'}>
          {player.invitedAt ? 'Resend invite' : 'Send invite'}
        </button>
      </div>
      {note && (
        <p role={note.kind === 'error' ? 'alert' : 'status'} className={`claim-invites__note claim-invites__note--${note.kind}`}>
          {note.text}
          {note.mergeAvailable && (
            <> <button type="button" className="link-button" onClick={() => save(true)}>Merge this player into that account</button></>
          )}
        </p>
      )}
    </li>
  );
}

/**
 * For the league host / team owner, managers, captains and admins: the
 * team's players who haven't joined Sportifide yet, their contact details and
 * claim invites, and a form to add a new player. Renders nothing for anyone
 * else (the API answers 401 / 403).
 */
function ClaimInvitesPanel({ teamId }) {
  const [state, setState] = useState({ loaded: false, allowed: false, players: [] });
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [note, setNote] = useState(null);

  const load = useCallback(() => {
    if (!teamId) return;
    listPlaceholderPlayers(teamId)
      .then((res) => setState({ loaded: true, allowed: true, players: res.data?.players || [] }))
      .catch(() => setState({ loaded: true, allowed: false, players: [] }));
  }, [teamId]);

  useEffect(() => { load(); }, [load]);

  if (!state.loaded || !state.allowed) return null;

  const add = async (e) => {
    e.preventDefault();
    setNote(null);
    try {
      const res = await addPlaceholderPlayer(teamId, { ...form, sendInvite: Boolean(form.email) });
      setNote({ kind: 'ok', text: res.message || 'Player added' });
      setForm({ name: '', email: '', phone: '' });
      load();
    } catch (err) {
      setNote({ kind: 'error', text: msg(err, 'Could not add the player') });
    }
  };

  return (
    <section className="league-team-section claim-invites" aria-label="Players who haven't joined">
      <h2 className="league-team-section__title">Players who haven't joined yet</h2>
      <p className="league-team-page__note">
        Add an email and send a claim link: they create their account (or sign in) and take over this roster spot and its match history.
      </p>
      {state.players.length ? (
        <ul className="claim-invites__list">
          {state.players.map((p) => <PlaceholderRow key={p.id} teamId={teamId} player={p} onChanged={load} />)}
        </ul>
      ) : (
        <p className="league-team-page__note">Everyone on this team has joined.</p>
      )}
      <form className="claim-invites__add" onSubmit={add}>
        <h3>Add a player</h3>
        <input aria-label="New player name" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        <input aria-label="New player email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <input aria-label="New player phone" type="tel" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        <button type="submit">Add player</button>
        {note && <p role={note.kind === 'error' ? 'alert' : 'status'} className={`claim-invites__note claim-invites__note--${note.kind}`}>{note.text}</p>}
      </form>
    </section>
  );
}

export default ClaimInvitesPanel;
